import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Building2, CreditCard, Users, TrendingUp, AlertTriangle, Clock, Sparkles, MessageCircle, HardDrive } from "lucide-react";
import PlatformClinicsPanel from "@/components/PlatformClinicsPanel";
import PlatformAuthHealthPanel from "@/components/PlatformAuthHealthPanel";
import InstagramComposerPanel from "@/components/InstagramComposerPanel";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function PlatformAdmin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user && !user.is_platform_admin) return;   // do not fetch — will redirect below
    const load = () => api.get("/platform/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [user]);

  // Client-side guard — server also enforces via is_platform_admin on every
  // /api/platform/* endpoint. This just avoids a flashed "loading" state
  // for users who shouldn't be here at all.
  if (user && !user.is_platform_admin) {
    return <Navigate to="/app" replace />;
  }

  if (!stats) return <div className="p-8 text-sm text-ink-soft">Loading platform stats…</div>;

  // Memoized derivations for the plan-mix chart — avoids recomputing on every render.
  const planMixTotal = Object.values(stats.plan_mix).reduce((s, n) => s + n, 0) || 1;
  const sortedPlanMix = Object.entries(stats.plan_mix).sort((a, b) => b[1] - a[1]);

  return (
    <div className="min-h-screen bg-linen text-ink" data-testid="platform-admin">
      <header className="border-b border-[#E8EAE9] bg-white">
        <div className="container-lg py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/viyora-icon-64.png" alt="Viyora" className="w-9 h-9 " />
            <div>
              <div className="font-display text-xl leading-none">Viyora</div>
              <div className="text-[10px] uppercase tracking-widest text-ink-soft mt-1">Founder Admin</div>
            </div>
          </div>
          <Link to="/app" className="text-xs text-ink-mid hover:text-ink">← Back to my clinic</Link>
        </div>
      </header>

      <main className="container-lg py-10">
        <span className="overline text-ink-soft">Overview</span>
        <h1 className="mt-2 font-display text-4xl md:text-5xl tracking-tight">The whole show, at a glance.</h1>

        {/* Headline stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <Stat icon={CreditCard}  label="MRR"                value={INR(stats.mrr_inr)}                       highlight testid="stat-mrr" />
          <Stat icon={Building2}   label="Total clinics"      value={stats.totals.clinics}                     testid="stat-clinics" />
          <Stat icon={Clock}       label="Active trials"      value={stats.totals.active_trials}               testid="stat-trials" />
          <Stat icon={TrendingUp}  label="Paid clinics"       value={stats.totals.paid_clinics}                testid="stat-paid" />
          <Stat icon={Users}       label="Total users"        value={stats.totals.users}                       testid="stat-users" />
          <Stat icon={AlertTriangle} label="Trials expiring · 7d" value={stats.totals.trials_expiring_7d}     warn={stats.totals.trials_expiring_7d > 0} testid="stat-expiring" />
          <Stat icon={AlertTriangle} label="Payment failures · 30d" value={stats.totals.payment_failures_30d} warn={stats.totals.payment_failures_30d > 0} testid="stat-fails" />
        </div>

        {/* Plan mix */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="card-widget lg:col-span-2">
            <div className="overline text-ink-soft mb-4">Plan distribution</div>
            <div className="space-y-3">
              {sortedPlanMix.map(([plan, count]) => {
                const pct = Math.round((count / planMixTotal) * 100);
                return (
                  <div key={plan} data-testid={`plan-row-${plan}`}>
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{plan.replace(/_/g, " ")}</span>
                      <span className="text-ink-mid">{count} · {pct}%</span>
                    </div>
                    <div className="mt-1 h-2 bg-linen rounded-full overflow-hidden">
                      <div className="h-full bg-pine transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card-widget">
            <div className="overline text-ink-soft mb-4">Platform usage</div>
            <UsageRow icon={Sparkles}      label="AI requests"       value={stats.usage.ai_requests.toLocaleString("en-IN")} />
            <UsageRow icon={MessageCircle} label="WhatsApp sent"     value={stats.usage.whatsapp_msgs.toLocaleString("en-IN")} />
            <UsageRow icon={HardDrive}     label="Storage"           value={`${stats.usage.storage_mb.toLocaleString("en-IN")} MB`} />
          </div>
        </div>

        {/* Recent signups */}
        <div className="card-widget mt-10">
          <div className="overline text-ink-soft mb-3">Recent signups</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-soft overline">
                <th className="py-2">Clinic</th><th>Plan</th><th>Status</th><th>Trial ends</th><th className="text-right">Joined</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_signups.map((c) => (
                <tr key={c.id} className="border-t border-[#E8EAE9]" data-testid={`recent-${c.id}`}>
                  <td className="py-3 font-medium">{c.name}<span className="text-ink-soft font-mono text-xs ml-2">/{c.slug}</span></td>
                  <td className="text-ink-mid">{c.plan || "—"}</td>
                  <td>
                    <span className={`text-xs px-2.5 py-1 rounded-full ${c.subscription_status === "trialing" ? "bg-[#FDF3ED] text-[#8A3F1E]" : "bg-[#E8F0EB] text-[#2D6A4F]"}`}>
                      {c.subscription_status || "—"}
                    </span>
                  </td>
                  <td className="text-ink-mid text-xs">{c.trial_ends_at ? new Date(c.trial_ends_at).toLocaleDateString("en-IN") : "—"}</td>
                  <td className="text-right text-ink-mid text-xs">{new Date(c.created_at).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 text-xs text-ink-soft text-center">
          Generated at {new Date(stats.generated_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })} · auto-refreshes every 30 s
        </div>

        {/* Founder-only auth health monitor — spot production auth issues
            within minutes without digging through logs. */}
        <PlatformAuthHealthPanel />

        {/* Founder-only clinic management. Appended below the existing
            analytics dashboard — no redesign of the header/stats above. */}
        <PlatformClinicsPanel />

        {/* Founder-only Instagram composer. Uses server-side META creds; the
            frontend never touches the access token. */}
        <InstagramComposerPanel />
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, highlight, warn, testid }) {
  return (
    <div className={`rounded-2xl p-6 border ${highlight ? "bg-pine text-linen border-pine" : warn ? "bg-[#FCECEC] border-[#EDC7C6]" : "bg-white border-[#E8EAE9]"}`} data-testid={testid}>
      <Icon className={`w-4 h-4 ${highlight ? "text-linen/70" : warn ? "text-[#B94A48]" : "text-ink-mid"}`} />
      <div className={`mt-3 font-display text-4xl ${warn ? "text-[#B94A48]" : ""}`}>{value}</div>
      <div className={`mt-1 text-xs uppercase tracking-widest ${highlight ? "text-linen/70" : "text-ink-soft"}`}>{label}</div>
    </div>
  );
}

function UsageRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#F1F2F0] last:border-b-0">
      <div className="flex items-center gap-2 text-sm text-ink-mid">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="font-display text-xl">{value}</div>
    </div>
  );
}
